import type { ProcessingErrorTyped } from '../types';

interface AxiosLikeError {
  isAxiosError?: boolean;
  code?: string;
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
}

const isObject = (val: unknown): val is Record<string, unknown> => typeof val === 'object' && val !== null;

const isAxiosError = (val: unknown): val is AxiosLikeError => isObject(val) && val.isAxiosError === true;

const lower = (val: unknown): string => (typeof val === 'string' ? val.toLowerCase() : '');

const messageOf = (err: unknown, fallback: string): string => {
  if (typeof err === 'string') return err;
  if (isObject(err) && typeof err.message === 'string') return err.message;
  return fallback;
};

const extractResponseMessage = (err: AxiosLikeError): string => {
  const data = err.response?.data;
  if (typeof data === 'string') return data;
  if (isObject(data) && typeof data.message === 'string') {
    return data.message;
  }
  return '';
};

const isTimeoutHaystack = (haystack: string, code: string): boolean =>
  code === 'ECONNABORTED' || haystack.includes('timeout');

const isQuotaHaystack = (haystack: string, status: number | undefined): boolean =>
  status === 429 || haystack.includes('quota') || haystack.includes('rate limit');

const isFileInvalid = (haystack: string, status: number | undefined): boolean => {
  if (typeof status !== 'number' || status < 400 || status >= 500) return false;
  return (
    haystack.includes('file') ||
    haystack.includes('archivo') ||
    haystack.includes('invalid') ||
    haystack.includes('unsupported')
  );
};

/**
 * Clasifica errores de axios (con response o code) en categorías tipadas.
 * @param {AxiosLikeError} err Error de axios.
 * @returns {ProcessingErrorTyped} Resultado tipado.
 */
const classifyAxios = (err: AxiosLikeError): ProcessingErrorTyped => {
  const code = err.code ?? '';
  const msg = err.message ?? '';
  const status = err.response?.status;
  const respMessage = extractResponseMessage(err);
  const haystack = `${lower(msg)} ${lower(respMessage)}`;

  if (isTimeoutHaystack(haystack, code)) {
    return {
      kind: 'timeout',
      message: msg || 'La operación excedió el tiempo de espera.',
      retryable: true,
    };
  }

  if (isQuotaHaystack(haystack, status)) {
    return {
      kind: 'quota',
      message: respMessage || msg || 'Se alcanzó el límite de uso.',
      retryable: false,
    };
  }

  if (code === 'ERR_NETWORK' || !err.response) {
    return {
      kind: 'network',
      message: msg || 'Error de red.',
      retryable: true,
    };
  }

  if (isFileInvalid(haystack, status)) {
    return {
      kind: 'file_invalid',
      message: respMessage || msg || 'El archivo no es válido.',
      retryable: false,
    };
  }

  return {
    kind: 'unknown',
    message: respMessage || msg || 'Error desconocido.',
    retryable: true,
  };
};

/**
 * Clasifica un Error nativo (no axios) buscando patrones en su mensaje.
 * @param {Error} err Error nativo.
 * @returns {ProcessingErrorTyped | null} Clasificación o null si no matchea.
 */
const classifyNativeError = (err: Error): ProcessingErrorTyped | null => {
  const haystack = lower(err.message);
  if (haystack.includes('timeout')) {
    return { kind: 'timeout', message: err.message, retryable: true };
  }
  if (haystack.includes('quota') || haystack.includes('rate limit')) {
    return { kind: 'quota', message: err.message, retryable: false };
  }
  if (haystack.includes('network') || haystack.includes('fetch failed')) {
    return { kind: 'network', message: err.message, retryable: true };
  }
  if (haystack.includes('json') || haystack.includes('parse')) {
    return { kind: 'parsing', message: err.message, retryable: true };
  }
  return null;
};

/**
 * Clasifica un error arbitrario en una categoría procesable por la UI.
 * @param {unknown} err Error capturado (axios, Error, string, etc.).
 * @returns {ProcessingErrorTyped} Estructura tipada con kind, mensaje y retryable.
 */
export const classifyError = (err: unknown): ProcessingErrorTyped => {
  if (isAxiosError(err)) return classifyAxios(err);

  if (err instanceof SyntaxError) {
    return {
      kind: 'parsing',
      message: err.message || 'No se pudo parsear la respuesta.',
      retryable: true,
    };
  }

  if (err instanceof Error) {
    const matched = classifyNativeError(err);
    if (matched) return matched;
  }

  return {
    kind: 'unknown',
    message: messageOf(err, 'Error desconocido.'),
    retryable: true,
  };
};
